<template>
  <div class="application-view-container">
    <header class="page-header">
      <div class="header-left">
        <h1>View Application</h1>
        <router-link to="/admin" class="back-link">← Back to Dashboard</router-link>
      </div>
      <div class="header-actions">
        <button class="print-btn" @click="downloadPDF" v-if="applicationData">📄 Download PDF</button>
        <button 
          class="convert-employee-btn" 
          @click="convertToEmployee"
          :disabled="converting"
          v-if="applicationData && applicationData.status === 'Converted to Employee'"
        >
          {{ converting ? 'Converting...' : 'Convert to Employee' }}
        </button>
        <button class="delete-app-btn" @click="confirmDeleteApplication" v-if="applicationData">
          Delete Application
        </button>
      </div>
    </header>

    <div v-if="loading" class="loading-state">
      Loading application…
    </div>

    <div v-else-if="!applicationData" class="error-state">
      <p>Application not found or you don't have permission to view it.</p>
    </div>

    <div v-if="conversionSuccess" class="conversion-success">
      <p>✅ Application successfully converted to employee record!</p>
    </div>

    <!-- Wrap printable content - only render when applicationData exists -->
    <div v-else-if="applicationData" id="print-section">
      <h1 v-if="isPrintingPDF" class="pdf-title">RTH Cleaners Applicant Information</h1>

      <div class="application-details">
        <!-- ====== Section: Basic Personal Info ====== -->
        <section class="details-card">
          <h2>Personal Information</h2>
          <dl>
            <dt>First Name:</dt>
            <dd>{{ applicationData.firstName || '—' }}</dd>

            <dt>Middle Name:</dt>
            <dd>{{ applicationData.middleName || '—' }}</dd>

            <dt>Last Name:</dt>
            <dd>{{ applicationData.lastName || '—' }}</dd>
            <dt>Date of Birth:</dt>
            <dd>{{ applicationData.dateOfBirth || '—' }}</dd>

            <dt>SSN:</dt>
            <dd>
              <template v-if="applicationData.ssn">
                <span class="ssn-value">{{ showSSN && !isPrintingPDF ? applicationData.ssn : maskSSN(applicationData.ssn) }}</span>
                <button v-if="!isPrintingPDF" type="button" class="ssn-toggle" @click="showSSN = !showSSN"
                  aria-label="Toggle SSN visibility">
                  {{ showSSN ? 'Hide' : 'Show' }}
                </button>
              </template>
              <template v-else>—</template>
            </dd>

            <dt>Address:</dt>
            <dd>{{ applicationData.address || '—' }}</dd>

            <dt>City:</dt>
            <dd>{{ applicationData.city || '—' }}</dd>
            <dt>Country:</dt>
            <dd>{{ applicationData.country || '—' }}</dd>

            <dt>State:</dt>
            <dd>{{ applicationData.state || '—' }}</dd>

            <dt>Zip Code:</dt>
            <dd>{{ applicationData.zip || '—' }}</dd>

            <dt>Cell Phone:</dt>
            <dd>{{ applicationData.cell || '—' }}</dd>

            <dt>WhatsApp:</dt>
            <dd>{{ applicationData.whatsapp || '—' }}</dd>
          </dl>
        </section>

        <!-- ====== Section: Contact & Emergency ====== -->
        <section class="details-card">
          <h2>Contact & Emergency</h2>
          <dl>
            <dt>Email:</dt>
            <dd>{{ applicationData.email || '—' }}</dd>

            <dt>Emergency Contact Name:</dt>
            <dd>{{ applicationData.emergencyContactName || '—' }}</dd>

            <dt>Emergency Contact Phone:</dt>
            <dd>{{ applicationData.emergencyContactPhone || '—' }}</dd>
          </dl>
        </section>

        <!-- ====== Section: Position & Employment ====== -->
        <section class="details-card">
          <h2>Position & Employment History</h2>
          <dl>
            <dt>Position Applying For:</dt>
            <dd>{{ applicationData.positionapplying || '—' }}</dd>

            <dt>Other Position:</dt>
            <dd>{{ applicationData.otherPosition || '—' }}</dd>

            <dt>Company Selected:</dt>
            <dd>{{ applicationData.companySelected || '—' }}</dd>

            <dt>Employer Name:</dt>
            <dd>{{ applicationData.employerName || '—' }}</dd>

            <dt>Date Employed:</dt>
            <dd>{{ applicationData.dateEmployed || '—' }}</dd>

            <dt>Date Unemployed:</dt>
            <dd>{{ applicationData.dateUnemployed || '—' }}</dd>

            <dt>May Contact Employer:</dt>
            <dd>{{ applicationData.mayContactEmployer || '—' }}</dd>

            <dt>Drug Test:</dt>
            <dd>{{ applicationData.drugTest || '—' }}</dd>

            <dt>Weekend Work:</dt>
            <dd>{{ applicationData.weekendWork || '—' }}</dd>

            <dt>Felony:</dt>
            <dd>{{ applicationData.felony || '—' }}</dd>
          </dl>
        </section>

        <!-- ====== Section: Education (Array with Count) ====== -->
        <section class="details-card" v-if="applicationData.education && applicationData.education.length">
          <h2>Education History</h2>
          <div v-for="(edu, index) in applicationData.education" :key="index" class="education-entry">
            <!-- Row count -->
            <p class="edu-heading">Education {{ index + 1 }}</p>
            <dl>
              <dt>School Name:</dt>
              <dd>{{ edu.schoolName || '—' }}</dd>

              <dt>School Location:</dt>
              <dd>{{ edu.schoolLocation || '—' }}</dd>

              <dt>Degree Received:</dt>
              <dd>{{ edu.degreeReceived || '—' }}</dd>

              <dt>Major:</dt>
              <dd>{{ edu.major || '—' }}</dd>

              <dt>Years Attended:</dt>
              <dd>{{ edu.yearsAttended || '—' }}</dd>
            </dl>
          </div>
        </section>

        <!-- ====== Section: References (with Count) ====== -->
        <section class="details-card">
          <h2>References</h2>
          <dl>
            <!-- Reference 1 row count in heading -->
            <dt>Reference 1 Name:</dt>
            <dd>{{ applicationData.reference1Name || '—' }}</dd>

            <dt>Reference 1 Company:</dt>
            <dd>{{ applicationData.reference1Company || '—' }}</dd>

            <dt>Reference 1 Title:</dt>
            <dd>{{ applicationData.reference1Title || '—' }}</dd>

            <dt>Reference 1 Letter:</dt>
            <dd>
              <i v-if="applicationData.reference1" class="fas fa-file-pdf pdf-icon"
                @click="openReferenceModal(applicationData.reference1)" title="View Reference 1 Letter"></i>

              <span v-else>—</span>
            </dd>

            <!-- Reference 2 row count in heading -->
            <dt>Reference 2 Name:</dt>
            <dd>{{ applicationData.reference2Name || '—' }}</dd>

            <dt>Reference 2 Company:</dt>
            <dd>{{ applicationData.reference2Company || '—' }}</dd>

            <dt>Reference 2 Title:</dt>
            <dd>{{ applicationData.reference2Title || '—' }}</dd>

            <dt>Reference 2 Letter:</dt>
            <dd>
              <i v-if="applicationData.reference2" class="fas fa-file-pdf pdf-icon"
                @click="openReferenceModal(applicationData.reference2)" title="View Reference 2 Letter"></i>
              <span v-else>—</span>
            </dd>
          </dl>
        </section>

        <!-- ====== Section: Passport & Documents ====== -->
        <!-- Page break before Passport section -->
        <div :class="{ 'pdf-page-break': isPrintingPDF }"></div>
        <section class="details-card">
          <h2>Passport & Documents</h2>
          <div class="doc-grid">
            <div class="doc-pair">
              <strong>Passport Number:</strong>
              <span>{{ applicationData.passportnum || '—' }}</span>
            </div>

            <div class="doc-pair">
              <strong>Passport Expiration Date:</strong>
              <span>{{ applicationData.passportexpirationdate || '—' }}</span>
            </div>

            <div class="doc-pair full avoid-break">
              <strong>Passport Image:</strong>
              <div v-if="applicationData.passport">
                <img :src="passportImageDataUrl || applicationData.passport" alt="Passport" class="passport-thumbnail"
                  @click="showPassportModal = true" />
              </div>
              <div v-else>—</div>
            </div>

            <div class="doc-pair">
              <strong>Resume:</strong>
              <span v-if="applicationData.resume">
                <i class="fas fa-file-pdf pdf-icon" @click="openReferenceModal(applicationData.resume)"
                  title="View Resume"></i>
              </span>
              <span v-else>—</span>
            </div>

            <div class="doc-pair full">
              <strong>Applicant Signature:</strong>
              <div v-if="applicationData.applicantSignatureDataUrl">
                <img :src="applicationData.applicantSignatureDataUrl" alt="Signature" class="signature-img" />
              </div>
              <div v-else>—</div>
            </div>
          </div>

        </section>

        <!-- ====== Section: Authorizations & Acknowledgments ====== -->
        <section class="details-card">
          <h2>Authorizations & Acknowledgments</h2>
          <dl>
            <dt>Authorize Investigation:</dt>
            <dd>{{ boolToYesNo(applicationData.authorizeInvestigation) }}</dd>

            <dt>Certify Truth:</dt>
            <dd>{{ boolToYesNo(applicationData.certifyTruth) }}</dd>

            <dt>Understand Consequence:</dt>
            <dd>{{ boolToYesNo(applicationData.understandConsequence) }}</dd>
          </dl>
        </section>

        <!-- ====== Section: Metadata ====== -->
        <section class="details-card">
          <h2>Metadata</h2>
          <dl>
            <dt>Application Date:</dt>
            <dd>{{ applicationData.applicationDate || '—' }}</dd>

            <dt>Submitted At:</dt>
            <dd>{{ formatTimestamp(applicationData.submittedAt) || '—' }}</dd>
          </dl>
        </section>

        <!-- ====== Section: Application Status (Editable) ====== -->
        <section class="status-card" v-if="!isPrintingPDF">
          <h2>Application Status</h2>
          <div class="status-field">
            <label for="status-select">Current Status:</label>
            <select id="status-select" v-model="newStatus">
              <option value="Pending">Pending</option>
              <option value="InReview">In Review</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
              <option value="Interview Scheduled">Interview Scheduled</option>
              <option value="Interview Rejected">Interview Rejected</option>
              <option value="Interview Accepted">Interview Accepted</option>
              <option value="Agency Processing">Move to Agency Queue</option>
              <option value="Converted to Employee">Converted to Employee</option>
            </select>
          </div>

          <div class="form-group" v-if="newStatus !== applicationData.status">
            <label for="status-note" class="form-label">Status Update Notes</label>
            <textarea id="status-note" v-model="statusNote" rows="4" class="form-textarea"
              placeholder="Add notes explaining why the status was changed..."></textarea>
          </div>
          <div style="height: 1.5rem;"></div>
          
          <!-- Timeline section - only show when statusLogs exist -->
          <section class="timeline-wrapper" v-if="statusLogs.length">
            <h2 class="timeline-title">Application Notes</h2>
            <div class="timeline">
              <div v-for="log in statusLogs" :key="log.id" class="timeline-item">
                <div class="timeline-icon"></div>
                <div class="timeline-content">
                  <div class="timeline-date">{{ formatTimestamp(log.updatedAt) }}</div>
                  <div class="timeline-status">{{ log.status }}</div>
                  <div class="timeline-note">{{ log.note || '—' }}</div>
                  <div class="timeline-actions">
                    <i class="fas fa-edit action-icon" @click="editNote(log)"></i>
                    <i class="fas fa-trash action-icon delete" @click="deleteNote(log.id)"></i>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
            <div class="modal-window">
              <h3>Edit Note</h3>
              <textarea v-model="editedNote" rows="5" class="form-textarea" />
              <div class="modal-actions">
                <button class="btn-save" @click="saveEditedNote">Save</button>
                <button class="close-btn" @click="closeEditModal">Cancel</button>
              </div>
            </div>
          </div>

          <button class="btn-save" :disabled="saving || newStatus === applicationData.status" @click="updateStatus">
            {{ saving ? 'Saving…' : 'Save Status' }}
          </button>

          <p v-if="saveSuccess" class="success-msg">
            Status updated successfully!
          </p>
          <p v-if="saveError" class="error-msg">
            Error saving: {{ saveError }}
          </p>
        </section>
      </div>
    </div>

    <!-- Passport Modal - only show when applicationData exists -->
    <div v-if="showPassportModal && applicationData" class="modal-overlay" @click.self="showPassportModal = false">
      <div class="modal-window">
        <button class="modal-close" @click="showPassportModal = false">×</button>
        <div class="modal-image-wrapper">
          <img :src="applicationData.passport" alt="Passport Full" class="modal-image" />
        </div>
        <div class="modal-actions">
          <button class="download-btn" @click="downloadPassportImage">Download</button>
          <button class="close-btn" @click="showPassportModal = false">Close</button>
        </div>
      </div>
    </div>

    <!-- Reference Modal -->
    <div v-if="showReferenceModal" class="modal-overlay" @click.self="closeReferenceModal">
      <div class="modal-window">
        <button class="modal-close" @click="closeReferenceModal">×</button>
        <iframe :src="selectedReferenceUrl" class="pdf-viewer" frameborder="0" allowfullscreen></iframe>
        <div class="modal-actions">
          <button class="download-btn" @click="downloadReferenceFile">Download</button>
          <button class="close-btn" @click="closeReferenceModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { notifyError } from '@/services/notify'
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  addDoc,
  getDocs,
  orderBy,
  query
} from "firebase/firestore"
import Swal from "sweetalert2"
import html2pdf from 'html2pdf.js'

export default {
  name: "ApplicationView",
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      converting: false,
      conversionSuccess: false,
      applicationData: null,
      newStatus: "",
      statusNote: "",
      saveSuccess: false,
      saveError: null,
      showPassportModal: false,
      showReferenceModal: false,
      selectedReferenceUrl: "",
      statusLogs: [],
      showEditModal: false,
      editedNote: '',
      editingLogId: null,
      isPrintingPDF: false,
      passportImageDataUrl: null,
      showSSN: false,
    }
  },
  created() {
    this.fetchApplication()
  },
  methods: {
    maskSSN(ssn) {
      const digits = String(ssn).replace(/\D/g, '')
      return `•••-••-${digits.slice(-4)}`
    },
    async downloadPDF() {
      // Guard clause to ensure applicationData exists
      if (!this.applicationData) {
        console.error('Cannot generate PDF: applicationData is null')
        return
      }

      this.isPrintingPDF = true;

      // Ensure passport image is base64 before proceeding
      if (this.applicationData.passport && !this.passportImageDataUrl) {
        try {
          const response = await fetch(this.applicationData.passport);
          const blob = await response.blob();
          const reader = new FileReader();

          await new Promise((resolve, reject) => {
            reader.onloadend = () => {
              this.passportImageDataUrl = reader.result;
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.warn("Failed to fetch and convert passport image:", error);
        }
      }

      await this.$nextTick();
      const element = document.getElementById('print-section');

      const opt = {
        margin: 0.5,
        filename: `Application_${this.applicationData.firstName || 'Unknown'}_${this.applicationData.lastName || 'View'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      };

      await html2pdf().set(opt).from(element).save();
      this.isPrintingPDF = false;
    },

    async fetchApplication() {
      this.loading = true
      this.saveSuccess = false
      this.saveError = null

      try {
        const db = getFirestore()
        const docRef = doc(db, "applications", this.id)
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
          this.applicationData = { id: docSnap.id, ...docSnap.data() }
          this.newStatus = this.applicationData.status || "Pending"
          await this.fetchStatusLogs()
        } else {
          this.applicationData = null
        }
      } catch (err) {
        console.error("Error fetching application:", err)
        this.applicationData = null
      } finally {
        this.loading = false
      }
    },

    async convertToEmployee() {
      if (!this.applicationData) {
        console.error('Cannot convert: applicationData is null')
        return
      }

      const result = await Swal.fire({
        title: 'Convert to Employee?',
        html: `
          <p>This will create an employee record for:</p>
          <strong>${this.applicationData.firstName || 'Unknown'} ${this.applicationData.lastName || ''}</strong>
          <p>Are you sure you want to proceed?</p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Convert',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      })

      if (!result.isConfirmed) return

      this.converting = true
      this.conversionSuccess = false

      try {
        const db = getFirestore()
        
        // Generate employee ID
        const employeeId = this.generateEmployeeId()
        
        // Prepare employee data from application
        const employeeData = {
          // Personal Information
          employeeId: employeeId,
          firstName: this.applicationData.firstName || '',
          middleName: this.applicationData.middleName || '',
          lastName: this.applicationData.lastName || '',
          dateOfBirth: this.applicationData.dateOfBirth || '',
          ssn: this.applicationData.ssn || '',
          
          // Address Information
          address: this.applicationData.address || '',
          city: this.applicationData.city || '',
          state: this.applicationData.state || '',
          zipCode: this.applicationData.zip || '',
          country: this.applicationData.country || '',
          
          // Contact Information
          email: (this.applicationData.email || '').trim().toLowerCase(),
          cellPhone: this.applicationData.cell || '',
          whatsApp: this.applicationData.whatsapp || '',
          
          // Emergency Contact
          emergencyContactName: this.applicationData.emergencyContactName || '',
          emergencyContactPhone: this.applicationData.emergencyContactPhone || '',
          
          // Employment Information
          position: this.applicationData.positionapplying || '',
          companySelected: this.applicationData.companySelected || '',
          
          // Employment Status
          status: 'Active',
          hireDate: new Date().toISOString().split('T')[0], // Today's date
          
          // Document References
          passportNumber: this.applicationData.passportnum || '',
          passportExpirationDate: this.applicationData.passportexpirationdate || '',
          passportImageUrl: this.applicationData.passport || '',
          resumeUrl: this.applicationData.resume || '',
          signatureUrl: this.applicationData.applicantSignatureDataUrl || '',
          
          // Reference from application
          originalApplicationId: this.id,
          
          // Metadata
          createdAt: serverTimestamp(),
          createdBy: 'admin', // You can get this from auth if needed
          updatedAt: serverTimestamp(),
        }

        // Add employee to 'employees' collection
        const employeeRef = await addDoc(collection(db, 'employees'), employeeData)
        
        // Update application status to indicate conversion
        await updateDoc(doc(db, "applications", this.id), {
          status: 'Converted to Employee',
          convertedToEmployeeId: employeeRef.id,
          convertedAt: serverTimestamp(),
        })

        // Add status log entry
        const logRef = collection(doc(db, "applications", this.id), "statusLogs")
        await addDoc(logRef, {
          status: 'Converted to Employee',
          note: `Converted to employee with ID: ${employeeId}`,
          updatedAt: serverTimestamp()
        })

        // Update local state
        this.applicationData.status = 'Converted to Employee'
        this.newStatus = 'Converted to Employee'
        this.conversionSuccess = true
        
        // Refresh status logs
        await this.fetchStatusLogs()

        // Show success message
        await Swal.fire({
          title: 'Success!',
          html: `
            <p>Application converted to employee successfully!</p>
            <p><strong>Employee ID:</strong> ${employeeId}</p>
          `,
          icon: 'success',
          confirmButtonText: 'OK'
        })

      } catch (error) {
        console.error('Error converting to employee:', error)
        await Swal.fire({
          title: 'Error',
          text: 'Failed to convert application to employee. Please try again.',
          icon: 'error'
        })
      } finally {
        this.converting = false
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          this.conversionSuccess = false
        }, 5000)
      }
    },

    generateEmployeeId() {
      // Generate a unique employee ID (you can customize this format)
      const now = new Date()
      const year = now.getFullYear().toString().slice(-2)
      const month = (now.getMonth() + 1).toString().padStart(2, '0')
      const random = Math.floor(1000 + Math.random() * 9000)
      return `EMP${year}${month}${random}`
    },

    // ... rest of your existing methods remain the same ...
    openReferenceModal(url) {
      this.selectedReferenceUrl = url;
      this.showReferenceModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editedNote = '';
      this.editingLogId = null;
    },
    closeReferenceModal() {
      this.showReferenceModal = false;
      this.selectedReferenceUrl = null;
    },
    async downloadPassportImage() {
      if (!this.applicationData?.passport) return

      try {
        const imageUrl = this.applicationData.passport
        const response = await fetch(imageUrl)
        const blob = await response.blob()

        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = 'passport-image.jpg'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error("Download failed:", error)
        notifyError('The passport image could not be downloaded. Please try again.', 'Download failed')
      }
    },
    async downloadReferenceFile() {
      try {
        const response = await fetch(this.selectedReferenceUrl);
        const blob = await response.blob();

        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'reference-letter.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Download failed:", error);
        notifyError('The file could not be downloaded. Please try again.', 'Download failed');
      }
    },
    async fetchStatusLogs() {
      if (!this.id) return

      const db = getFirestore()
      const logsQuery = query(
        collection(doc(db, "applications", this.id), "statusLogs"),
        orderBy("updatedAt", "desc")
      )

      const logsSnap = await getDocs(logsQuery)
      this.statusLogs = logsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    },
    async updateStatus() {
      if (!this.applicationData) return

      this.saving = true
      this.saveSuccess = false
      this.saveError = null

      try {
        const db = getFirestore()
        const docRef = doc(db, "applications", this.id)

        await updateDoc(docRef, {
          status: this.newStatus,
          statusUpdatedAt: serverTimestamp(),
        })

        const logRef = collection(docRef, "statusLogs")
        await addDoc(logRef, {
          status: this.newStatus,
          note: this.statusNote || "",
          updatedAt: serverTimestamp()
        })

        this.applicationData.status = this.newStatus
        this.statusNote = ""
        this.saveSuccess = true
        await this.fetchStatusLogs()
      } catch (err) {
        console.error("Error updating status:", err)
        this.saveError = err.message || "Unknown error"
      } finally {
        this.saving = false
        setTimeout(() => {
          this.saveSuccess = false
        }, 3000)
      }
    },
    async editNote(log) {
      this.editedNote = log.note
      this.editingLogId = log.id
      this.showEditModal = true
    },
    async saveEditedNote() {
      const db = getFirestore()
      const logRef = doc(db, "applications", this.id, "statusLogs", this.editingLogId)
      await updateDoc(logRef, {
        note: this.editedNote,
        updatedAt: serverTimestamp()
      })
      this.showEditModal = false
      this.editedNote = ''
      this.editingLogId = null
      await this.fetchStatusLogs()
    },
    async deleteNote(logId) {
      const result = await Swal.fire({
        title: 'Delete this note?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel'
      })

      if (!result.isConfirmed) return

      try {
        const db = getFirestore()
        const logRef = doc(db, "applications", this.id, "statusLogs", logId)
        await deleteDoc(logRef)
        await this.fetchStatusLogs()
      } catch (err) {
        console.error("Error deleting note:", err)
        Swal.fire("Error", "Failed to delete the note.", "error")
      }
    },
    formatTimestamp(ts) {
      if (!ts) return ""
      const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts)
      return `${d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })} ${d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    },
    boolToYesNo(val) {
      if (val === true) return "Yes"
      if (val === false) return "No"
      return "—"
    },
    async confirmDeleteApplication() {
      if (!this.applicationData) return

      const result = await Swal.fire({
        title: '<strong>Are you sure?</strong>',
        html: 'Are  you sure you wish to delete this application.',
        icon: 'warning',
        showCancelButton: true,
        focusCancel: true,
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'custom-swal',
          confirmButton: 'swal-confirm-btn',
          cancelButton: 'swal-cancel-btn',
        }
      });

      if (!result.isConfirmed) return;

      try {
        const db = getFirestore();
        await deleteDoc(doc(db, "applications", this.id));

        await Swal.fire({
          title: 'Deleted!',
          text: 'The application has been removed.',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        });

        this.$router.push("/admin");
      } catch (err) {
        console.error("Error deleting application:", err);
        Swal.fire({
          title: "Error",
          text: "Failed to delete application. Please try again.",
          icon: "error",
        });
      }
    }
  },
}
</script>

<style scoped>

.ssn-value {
  font-family: monospace;
}

.ssn-toggle {
  margin-left: 0.5rem;
  background: none;
  border: none;
  padding: 0.125rem 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  cursor: pointer;
  border-radius: 0.25rem;
}

.ssn-toggle:hover {
  background: #f3f4f6;
  color: #374151;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
/* Convert to Employee button */
.convert-employee-btn {
  background-color: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.convert-employee-btn:hover:not(:disabled) {
  background-color: #059669;
}

.convert-employee-btn:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

/* Conversion success message */
.conversion-success {
  background-color: #d1fae5;
  border: 1px solid #10b981;
  color: #065f46;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  text-align: center;
}

.conversion-success p {
  margin: 0;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .header-actions {
    flex-direction: column;
    width: 100%;
  }

  .header-actions button {
    width: 100%;
  }
}

.application-view-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem;
}

/* Header now has two parts: title + delete button */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* “Back to Dashboard” link */
.back-link {
  font-size: 0.9rem;
  text-decoration: none;
  color: #5867dd;
}

/* “Delete Application” button styling */
.delete-app-btn {
  background-color: #dc2626;
  /* Red */
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: 4px;
  cursor: pointer;
}

.delete-app-btn:hover {
  background-color: #b91c1c;
}

/* SweetAlert2 overrides (optional) */
.swal2-confirm-button {
  background-color: #dc2626 !important;
  border: none !important;
  color: white !important;
}

.swal2-cancel-button {
  background-color: #6b7280 !important;
  /* Gray */
  border: none !important;
  color: white !important;
}

.loading-state,
.error-state {
  text-align: center;
  margin-top: 2rem;
}

/* Card styling for each section */
.details-card,
.status-card {
  background: #fff;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

.details-card h2,
.status-card h2 {
  margin-bottom: 0.75rem;
  font-size: 1.25rem;
  color: #1f2937;
}

/* Definition list styling */
dl {
  display: grid;
  grid-template-columns: 1fr 2fr;
  row-gap: 0.5rem;
  column-gap: 1rem;
}

dt {
  font-weight: 600;
  color: #374151;
}

dd {
  margin: 0;
  color: #4b5563;
}

/* Education entries (already numbered) */
.education-entry {
  border-top: 1px solid #e5e7eb;
  padding-top: 0.75rem;
  margin-top: 0.75rem;
}

.edu-heading {
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

/* Signature image styling */
.signature-img {
  max-width: 300px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-top: 0.5rem;
}

/* Status section */
.status-field {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.status-field label {
  font-weight: 600;
  color: #374151;
}

select {
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 1rem;
}

.btn-save {
  background-color: #5867dd;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.6rem 1.2rem;
  cursor: pointer;
  font-size: 1rem;
}

.btn-save:disabled {
  background-color: #a0a0a0;
  cursor: not-allowed;
}

.success-msg {
  margin-top: 0.75rem;
  color: #2f9e44;
}

.error-msg {
  margin-top: 0.75rem;
  color: #c92a2a;
}

/* SweetAlert2 Custom Modal */
.custom-swal {
  border-radius: 10px;
  padding: 1.5rem;
  font-family: 'Segoe UI', sans-serif;
}

.swal-confirm-btn {
  background-color: #3b82f6 !important;
  /* blue */
  color: white !important;
  padding: 0.6rem 1.2rem !important;
  font-weight: 600;
  border-radius: 6px !important;
  border: none !important;
}

.swal-cancel-btn {
  background-color: #f3f4f6 !important;
  color: #374151 !important;
  padding: 0.6rem 1.2rem !important;
  font-weight: 600;
  border-radius: 6px !important;
  border: none !important;
}



/* Responsive tweaks */
@media (max-width: 768px) {
  .application-view-container {
    padding: 1rem;
  }

  dl {
    grid-template-columns: 1fr;
  }

  .status-field {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}


.passport-thumbnail {
  max-width: 120px;
  cursor: pointer;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  max-width: 90%;
  max-height: 90%;
  overflow: auto;
  position: relative;
}

.modal-image {
  max-width: 100%;
  max-height: 70vh;
  margin-bottom: 1rem;
  border: 1px solid #ddd;
}

.download-btn {
  display: inline-block;
  background-color: #4caf50;
  color: white;
  padding: 0.5rem 1.2rem;
  margin-right: 1rem;
  text-decoration: none;
  border-radius: 6px;
  border: 1px solid #3e8e41;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.download-btn:hover {
  background-color: #45a049;
  transform: scale(1.03);
}


.close-btn {
  background-color: #dc2626;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-window {
  background: #fff;
  border-radius: 10px;
  padding: 1.5rem;
  width: 90vw;
  /* Use 90% of the viewport width */
  max-width: 1000px;
  /* Allow it to grow up to 1000px wide */
  max-height: 90vh;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pdf-viewer {
  width: 100%;
  height: 70vh;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.pdf-icon {
  color: #dc2626;
  font-size: 1.5rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.pdf-icon:hover {
  transform: scale(1.1);
}


.form-select,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  font-size: 0.95rem;
  border-radius: 6px;
  border: 1px solid #ccc;
  transition: border-color 0.2s;
}

.form-select:focus,
.form-textarea:focus {
  border-color: #4a90e2;
  outline: none;
}

.timeline-wrapper {
  background-color: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-top: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.timeline-title {
  font-size: 1.4rem;
  margin-bottom: 20px;
  font-weight: 600;
  color: #1f2937;
}

.timeline {
  position: relative;
  margin-left: 20px;
  padding-left: 20px;
  border-left: 3px solid #5867dd;
}

.timeline-item {
  position: relative;
  padding: 12px 0 12px 20px;
}

.timeline-icon {
  width: 12px;
  height: 12px;
  background-color: #5867dd;
  border-radius: 50%;
  position: absolute;
  left: -27px;
  top: 18px;
  border: 3px solid #fff;
  box-shadow: 0 0 0 2px #5867dd;
}

.timeline-content {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.timeline-date {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 6px;
}

.timeline-status {
  font-weight: 600;
  font-size: 1.05rem;
  color: #111827;
  margin-bottom: 4px;
}

.timeline-note {
  font-size: 0.95rem;
  color: #374151;
  line-height: 1.4;
}

.timeline-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.icon-button {
  background: transparent;
  border: none;
  color: #4b5563;
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.2s;
}

.icon-button:hover {
  color: #dc2626;
}

.print-btn {
  background-color: #4a90e2;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
}

.print-btn:hover {
  background-color: #2563eb;
}

.doc-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 2rem;
}

.doc-pair {
  display: flex;
  flex-direction: column;
}

.doc-pair.full {
  grid-column: span 2;
}

.doc-pair strong {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
}

.passport-thumbnail {
  max-width: 200px;
  max-height: 150px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.pdf-header {
  text-align: center;
  margin-bottom: 1rem;
  border-bottom: 2px solid #ddd;
  padding-bottom: 0.2rem;
}

.pdf-header h1 {
  font-size: 1.5rem;
  color: #1f2937;
  font-weight: bold;
}

.pdf-title {
  text-align: center;
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 2rem;
}

@media print {
  .avoid-break {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
}
.pdf-page-break {
  page-break-before: always;
  break-before: always;
}


</style>
